import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  CheckCheck,
  CheckCircle2,
  Eye,
  EyeOff,
  FileSpreadsheet,
  Inbox,
  PauseCircle,
  PlayCircle,
  UserCheck,
  UserX,
  Wallet,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@tsi/ui";
import {
  notificacoesApi,
  useNotificacoes,
  type Notificacao,
  type TipoNotificacao,
} from "@/features/notificacoes/notificacoes.api";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Ref do botão âncora — evita fechar o popover quando o usuário clica nele */
  anchorRef?: React.RefObject<HTMLElement | null>;
}

const TOM = {
  marca: "bg-brand/10 text-brand",
  verde: "bg-brand-green-50 text-brand-green-700",
  ambar: "bg-amber-50 text-amber-700",
  rosa: "bg-rose-50 text-rose-700",
};

const VISUAL: Record<TipoNotificacao, { icone: LucideIcon; tom: string }> = {
  reserva_nova: { icone: Inbox, tom: TOM.marca },
  reserva_confirmada: { icone: CheckCircle2, tom: TOM.verde },
  reserva_parcial: { icone: CheckCircle2, tom: TOM.verde },
  reserva_recusada: { icone: XCircle, tom: TOM.rosa },
  comissao_paga: { icone: Wallet, tom: TOM.verde },
  estoque_importado: { icone: FileSpreadsheet, tom: TOM.marca },
  estoque_pausado: { icone: PauseCircle, tom: TOM.ambar },
  estoque_retomado: { icone: PlayCircle, tom: TOM.verde },
  carta_desativada: { icone: EyeOff, tom: TOM.ambar },
  carta_ativada: { icone: Eye, tom: TOM.verde },
  parceiro_primeiro_acesso: { icone: UserCheck, tom: TOM.marca },
  parceiro_conta_excluida: { icone: UserX, tom: TOM.rosa },
};

function tempoRelativo(at: string): string {
  const min = Math.floor((Date.now() - new Date(at).getTime()) / 60_000);
  if (min < 1) return "agora";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h`;
  const d = Math.floor(h / 24);
  return d === 1 ? "ontem" : `${d} d`;
}

export function NotificationsPopover({ open, onOpenChange, anchorRef }: Props) {
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { data, error, mutate } = useNotificacoes();

  // Fecha ao clicar fora ou pressionar Esc
  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      const target = e.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (anchorRef?.current?.contains(target)) return;
      onOpenChange(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onOpenChange(false);
    }
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onOpenChange, anchorRef]);

  // Abriu o sino: busca na hora, sem esperar os 30 s.
  useEffect(() => {
    if (open) void mutate();
  }, [open, mutate]);

  if (!open) return null;

  const naoLidas = data?.naoLidas ?? 0;

  function abrir(n: Notificacao) {
    if (!n.lida && data) {
      // Marca na tela na hora; o servidor confirma em seguida.
      void mutate(
        notificacoesApi.marcarLida(n.id).then(() => undefined),
        {
          optimisticData: {
            ...data,
            naoLidas: Math.max(0, data.naoLidas - 1),
            itens: data.itens.map((i) => (i.id === n.id ? { ...i, lida: true } : i)),
          },
          populateCache: false,
          revalidate: true,
        },
      );
    }
    onOpenChange(false);
    if (n.link) navigate(n.link);
  }

  function marcarTodas() {
    if (!data) return;
    void mutate(
      notificacoesApi.marcarTodas().then(() => undefined),
      {
        optimisticData: { ...data, naoLidas: 0, itens: data.itens.map((i) => ({ ...i, lida: true })) },
        populateCache: false,
        revalidate: true,
      },
    );
  }

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-full z-50 mt-2 w-[400px] max-w-[calc(100vw-2rem)] origin-top-right overflow-hidden rounded-2xl border border-border/70 bg-card shadow-soft animate-in fade-in-0 zoom-in-95"
      role="dialog"
      aria-label="Notificações"
    >
      <div className="flex items-center justify-between gap-3 border-b border-border/60 px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand/10 text-brand">
            <Bell className="h-4 w-4" />
          </div>
          <div>
            <div className="text-[14px] font-semibold leading-tight">Notificações</div>
            <p className="text-[11px] text-muted-foreground">
              {!data
                ? "Carregando…"
                : naoLidas > 0
                  ? `${naoLidas} não ${naoLidas === 1 ? "lida" : "lidas"}`
                  : "Tudo lido"}
            </p>
          </div>
        </div>
        {naoLidas > 0 && (
          <button
            type="button"
            onClick={marcarTodas}
            className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11.5px] font-medium text-brand transition-colors hover:bg-brand/5"
          >
            <CheckCheck className="h-3.5 w-3.5" />
            Marcar todas como lidas
          </button>
        )}
      </div>

      <div className="max-h-[70vh] overflow-y-auto">
        {!data && !error ? (
          <ul className="divide-y divide-border/60" aria-hidden>
            {Array.from({ length: 4 }, (_, i) => (
              <li key={i} className="flex items-start gap-3 px-4 py-3">
                <div className="h-7 w-7 shrink-0 animate-pulse rounded-md bg-secondary" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 w-2/5 animate-pulse rounded bg-secondary" />
                  <div className="h-2.5 w-4/5 animate-pulse rounded bg-secondary" />
                </div>
              </li>
            ))}
          </ul>
        ) : error && !data ? (
          <div className="px-5 py-10 text-center text-[12px] text-muted-foreground">
            Não foi possível carregar as notificações agora. Tente de novo em instantes.
          </div>
        ) : data && data.itens.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-5 py-12 text-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-muted-foreground">
              <Bell className="h-4 w-4" />
            </div>
            <div className="mt-3 text-[13px] font-medium">Nada por aqui</div>
            <p className="mt-1 text-[11.5px] text-muted-foreground">
              Reservas novas, decisões e mudanças no estoque aparecem aqui.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border/60">
            {data?.itens.map((n) => {
              const { icone: Icone, tom } = VISUAL[n.tipo] ?? { icone: Bell, tom: TOM.marca };
              return (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => abrir(n)}
                    className={cn(
                      "flex w-full items-start gap-3 px-4 py-2.5 text-left transition-colors hover:bg-secondary/50",
                      !n.lida && "bg-brand/[0.03]",
                    )}
                  >
                    <div className={cn("mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md", tom)}>
                      <Icone className="h-3.5 w-3.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className={cn("text-[12.5px] text-foreground", n.lida ? "font-medium" : "font-semibold")}>
                          {n.titulo}
                        </span>
                        <span className="flex shrink-0 items-center gap-1.5 text-[10.5px] tabular-nums text-muted-foreground">
                          {tempoRelativo(n.criadaEm)}
                          {!n.lida && <span className="h-1.5 w-1.5 rounded-full bg-brand" aria-label="não lida" />}
                        </span>
                      </div>
                      <div className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-muted-foreground">{n.texto}</div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {data?.temMais && (
          <div className="border-t border-border/60 px-4 py-2 text-center text-[10.5px] text-muted-foreground">
            Mostrando as 20 mais recentes
          </div>
        )}
      </div>
    </div>
  );
}
